import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useTransformField } from '@/composables/transformField'
import type { IndexConfigResponse } from '@/types/api/config'
import type { IndexSearchResponse, ShowMapResponse } from '@/types/api/search'
import type { SearchResultsMeta } from '@/types/search'

export const useAppStore = defineStore(
  'app',
  () => {
    const { makeQuasarOption, makeQuasarOptions } = useTransformField()

    const { locale } = useI18n()

    const config = ref<IndexConfigResponse['data']>()

    const configCountry = computed(() => config.value?.country)
    const configLocale = computed(() => config.value?.locale)
    const configTheme = computed(() => config.value?.theme)

    const currentLocale = computed(
      () =>
        (configLocale.value
          && locale.value !== configLocale.value.default
          && locale.value)
        || '',
    )

    const defaultCategory = computed(
      () => config.value?.categories.find((v) => v.default)?.slug,
    )
    const defaultCommercialization = computed(
      () => config.value?.commercializations.find((v) => v.default)?.slug,
    )
    const defaultSortingOption = computed(() =>
      makeQuasarOption(config.value?.sorting_options.find((v) => v.default)),
    )

    const optionsCategories = computed<string[]>(
      () => config.value?.categories.map((c) => c.slug) || [],
    )

    const optionsCommercializations = computed<string[]>(
      () => config.value?.commercializations.map((c) => c.slug) || [],
    )

    const optionsSortingOptions = computed(() =>
      makeQuasarOptions(config.value?.sorting_options),
    )

    const showOnlyMap = ref<boolean>(false)

    const loadingListingsMap = ref<boolean>(false)
    const loadingListingsMeta = ref<boolean>(true)
    const loadingListingsSearch = ref<boolean>(true)

    const responseListingsMap = ref<ShowMapResponse>()
    const responseListingsMeta = ref<SearchResultsMeta>()
    const responseListingsSearch = ref<IndexSearchResponse>()

    return {
      config,
      configCountry,
      configLocale,
      configTheme,
      currentLocale,
      defaultCategory,
      defaultCommercialization,
      defaultSortingOption,
      loadingListingsMap,
      loadingListingsMeta,
      loadingListingsSearch,
      optionsCategories,
      optionsCommercializations,
      optionsSortingOptions,
      responseListingsMap,
      responseListingsMeta,
      responseListingsSearch,
      showOnlyMap,
    }
  },
  {
    share: {
      enable: true,
      initialize: false,
      omit: [
        'config',
        'loadingListingsMeta',
        'loadingListingsMap',
        'loadingListingsSearch',
        'responseListingsMap',
        'responseListingsMeta',
        'responseListingsSearch',
        'showOnlyMap',
      ],
    },
  },
)
